import { Button } from "@/lib/components/Button/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { CompositionDto } from "@repo/api/editor";
import { IconCrop11, IconCrop32 } from "@tabler/icons-react";
import { useCallback } from "react";

export const ASPECT_RATIO_CONTROLS_ID = "aspect-ratio-controls";

export default function AspectRatioControls() {
  const aspectRatio = useEditorStore(
    (state) => state.video?.compositionJson.aspectRatio,
  );

  const updateVideo = useEditorStore((state) => state.service.updateVideo);

  const handleAspectRatioClick = useCallback(
    (aspectRatio: CompositionDto["aspectRatio"]) => {
      updateVideo((video) => (video.compositionJson.aspectRatio = aspectRatio));
    },
    [updateVideo],
  );

  if (!aspectRatio) {
    return null;
  }

  return (
    <div className="absolute right-0 bottom-full left-0 z-0 flex items-center justify-center">
      <div id={ASPECT_RATIO_CONTROLS_ID} className="mb-3 flex gap-1">
        <AspectRatioItem
          targetAspectRatio="16:9"
          currentAspectRatio={aspectRatio}
          onClick={handleAspectRatioClick}
        />
        <AspectRatioItem
          targetAspectRatio="9:16"
          currentAspectRatio={aspectRatio}
          onClick={handleAspectRatioClick}
        />
        <AspectRatioItem
          targetAspectRatio="1:1"
          currentAspectRatio={aspectRatio}
          onClick={handleAspectRatioClick}
        />
      </div>
    </div>
  );
}

type AspectRatioItemProps = {
  targetAspectRatio: CompositionDto["aspectRatio"];
  currentAspectRatio: CompositionDto["aspectRatio"];
  onClick: (aspectRatio: CompositionDto["aspectRatio"]) => void;
};

function AspectRatioItem({
  targetAspectRatio,
  currentAspectRatio,
  onClick,
}: AspectRatioItemProps) {
  const icons = {
    "16:9": <IconCrop32 stroke={1.5} />,
    "9:16": <IconCrop32 className="rotate-90" stroke={1.5} />,
    "1:1": <IconCrop11 stroke={1.7} className="scale-90" />,
  };

  const Icon = icons[targetAspectRatio];

  const isSelected = currentAspectRatio === targetAspectRatio;
  const backgroundColor = isSelected ? "bg-white" : "bg-transparent";
  const borderColor = isSelected ? "" : "border-transparent";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          className={`${backgroundColor} ${borderColor} [&_svg]:size-6`}
          onClick={() => onClick(targetAspectRatio)}
          variant="outline"
          size="icon"
        >
          {Icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{targetAspectRatio}</p>
      </TooltipContent>
    </Tooltip>
  );
}
